import { analyzeCommonality, type AnalyzeCommonalityRequest } from '@/entities/discovery';
import { useAbortableMutation } from '@/shared/hooks/use-abortable-mutation';
import { v4 as uuidv4 } from 'uuid';

import { useDiscoveryMessagesActions } from '../stores/discovery-message-store';
import { searchCreatorPendingAtom } from '../stores/mutation-atom';
import { usePendingDiscoveryActionActions } from '../stores/pending-discovery-action-store';
import type { SeedPost } from '../stores/seed-post-store';

type AnalyzeCommonalityMutationRequest = {
  discoverySessionId: string;
  seedPosts: SeedPost[];
};

export function useAnalyzeCommonality() {
  const { appendMessages } = useDiscoveryMessagesActions();
  const { addPendingAction, removePendingAction } = usePendingDiscoveryActionActions();

  return useAbortableMutation({
    mutationFn: ({ discoverySessionId, seedPosts }: AnalyzeCommonalityMutationRequest, signal) => {
      const request: AnalyzeCommonalityRequest = {
        post_id_list: seedPosts.map((post) => post.id),
      };

      return analyzeCommonality(discoverySessionId, request, signal);
    },
    onMutate: ({ discoverySessionId, seedPosts }) => {
      const pendingActionId = uuidv4();
      addPendingAction({
        id: pendingActionId,
        sessionId: discoverySessionId,
        type: 'commonality',
        seedPosts,
      });

      return { pendingActionId };
    },
    onSuccess: (response) => {
      appendMessages([response.assistant_message, response.user_message]);
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context) removePendingAction(context.pendingActionId);
    },
    scope: { id: 'discovery:chat' },
    pendingAtom: searchCreatorPendingAtom,
    requestKey: 'discovery:analyze-commonality',
  });
}
